import { useState } from 'react';
import axios from 'axios';
import { X, SpinnerGap, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import { useRecaptchaExecute } from '@/shared/recaptcha';

const API = `${""}/api`;

/**
 * Modal to request a password reset email. Shows a generic success message
 * to avoid account enumeration (the backend mirrors this behaviour).
 */
export default function ForgotPasswordModal({ open, onClose, initialEmail = '' }) {
  const [email, setEmail] = useState(initialEmail);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const execRecaptcha = useRecaptchaExecute();

  if (!open) return null;

  const reset = () => {
    setEmail(initialEmail);
    setLoading(false);
    setSent(false);
    setError('');
  };
  const handleClose = () => { reset(); onClose?.(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const recaptcha_token = await execRecaptcha('recover_password');
      await axios.post(`${API}/auth/recover-password`, { email: email.trim(), recaptcha_token });
      setSent(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Não foi possível enviar o pedido. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="forgot-password-modal">
      <div className="w-full max-w-[420px] bg-white rounded-xl shadow-2xl" style={{ fontFamily: "'Manrope', sans-serif" }}>
        <div className="flex items-start justify-between p-6 pb-4">
          <div>
            <h2 className="text-xl font-black tracking-tighter text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
              Recuperar palavra-passe
            </h2>
            <p className="text-[#6B7280] text-xs mt-1">Enviaremos um link de reposição para o seu email.</p>
          </div>
          <button type="button" onClick={handleClose} data-testid="forgot-password-close" className="text-[#6B7280] hover:text-[#111827] transition-colors p-1">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 pb-6">
          {sent ? (
            <div className="p-4 rounded-md bg-green-50 border border-green-200 text-green-800 flex items-start gap-3" data-testid="forgot-password-success">
              <CheckCircle size={20} weight="fill" className="mt-0.5 shrink-0" />
              <p className="text-sm leading-relaxed">
                Se o email existir, receberá em breve instruções para repor a palavra-passe. Verifique a caixa de spam/promoções.
              </p>
            </div>
          ) : (
            <>
              {error && (
                <div data-testid="forgot-password-error" className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-[#FF2B2B] text-xs flex items-start gap-2">
                  <WarningCircle size={16} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#6B7280] mb-1.5 block">Email</label>
                  <input
                    data-testid="forgot-password-email"
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
                    className="w-full px-3 py-2.5 border border-[#E5E7EB] rounded-md text-sm text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#0F1829] transition-all"
                  />
                </div>
                <button type="submit" disabled={loading} data-testid="forgot-password-submit"
                  className="w-full py-2.5 bg-[#0F1829] text-white text-sm font-medium rounded-md hover:bg-[#002480] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                  {loading ? <SpinnerGap size={18} className="animate-spin" /> : null}
                  {loading ? 'A enviar…' : 'Enviar link de recuperação'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
